import { AppLoading } from '@core/components/AppLoading';
import MomentUtils from '@date-io/moment';
import history from '@history';
import { createGenerateClassName, jssPreset, StylesProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import RenderTheme from 'app/RenderTheme';
import { requestNotificationService } from 'app/services/websocket';
import { create } from 'jss';
import jssExtend from 'jss-plugin-extend';
import rtl from 'jss-rtl';
import jwtDecode from 'jwt-decode';
import 'moment/locale/pt-br';
import React, { useEffect, useState } from 'react';
import Provider from 'react-redux/es/components/Provider';
import { Router } from 'react-router-dom';
import AppContext from './AppContext';
import { Auth } from './auth';
import routes from './fuse-configs/routesConfig';
import keycloak from './keycloak';
import './services/api/axios';
import { usuarioApi } from './services/api/usuarioApi';
import authService from './services/authService';
import store from './store';
import { GlobalProvider } from './store/globalcontext';
import { encrypt, getIpRede } from './utils/geralUtil';

const jss = create({
  ...jssPreset(),
  plugins: [...jssPreset().plugins, jssExtend(), rtl()],
  insertionPoint: document.getElementById('jss-insertion-point'),
});

const generateClassName = createGenerateClassName();

const AppContent = () => {
  const [theme, setTheme] = useState('');

  useEffect(() => {
    return () => {
      requestNotificationService.disconnect();
    };
  }, []);

  useEffect(() => {
    const KEY = '%REACT_APP_ENCRYPT_KEY%';

    let ipRedeEncrypted = encrypt('0.0.0.0', KEY).toString();
    localStorage.setItem('IP', ipRedeEncrypted);

    const getIpRede = async () => {
      const res = await fetch('https://api.ipify.org?format=json');
      const json = await res.json();

      ipRedeEncrypted = encrypt(json.ip, KEY).toString();
      localStorage.setItem('IP', ipRedeEncrypted);
    };

    getIpRede();
  }, []);

  const handleTokenChange = async (tokens) => {
    if (tokens?.token) {
      await buscarEmpresaContexto(tokens.token);

      authService.setSession(tokens?.token, tokens?.refreshToken);
      authService.emit('onAutoLogin');
    }
  };

  const validarPermissaoAcessoKeycloak = (response, accessToken) => {
    usuarioApi
      .validarPermissaoAcessoKeycloak(getIpRede())
      .then((resp) => {
        buscarDadosOnboarding(response, accessToken);
      })
      .catch((error) => {
        if (
          error?.response?.data?.errorKey?.includes('error.ip.bloqueado') ||
          error?.response?.data?.errorKey?.includes('error.usuario.periodo-bloqueado')
        ) {
          setTheme('SESSAO_BLOQUEADA');
        }
      });
  };

  const buscarEmpresaContexto = (accessToken) => {
    const tokenDecode = jwtDecode(accessToken);
    const requestUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/backend/usuario/buscar-empresa-contexto/${tokenDecode.sub}`;

    return new Promise((resolve, reject) => {
      let status;
      fetch(requestUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          responseType: 'text',
        },
      })
        .then((response) => {
          status = response.status;
          return response.text();
        })
        .then((response) => {
          if (status === 200 && response !== '') {
            localStorage.setItem('EC', encrypt(response));
          }

          validarPermissaoAcessoKeycloak(response, accessToken);
          resolve();
        })
        .catch((error) => reject(error));
    });
  };

  const buscarDadosOnboarding = (empresaId, accessToken) => {
    if (!empresaId || empresaId === '') {
      return new Promise((resolve, reject) => {
        setTheme('ONBOARDING');
        resolve();
      });
    }
    const requestUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/backend/empresas/onboarding/buscar`;

    return new Promise((resolve, reject) => {
      fetch(requestUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          responseType: 'text',
        },
      })
        .then((response) => response.json())
        .then((response) => {
          if (response === 'FINALIZADO') {
            setTheme('FINANCEIRO');
          } else {
            setTheme('ONBOARDING');
          }
          resolve();
        })
        .catch((error) => reject(error));
    });
  };

  const MaintenancePage = () => {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 text-center" style={{ width: '100%' }}>
        <div className="bg-white p-6 rounded-2xl shadow-lg" style={{ width: '100%', height: '100%' }}>
          <img
            src="assets/images/manutencao.jpeg"
            alt="Sistema em manutenção"
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
          />
          <h1 className="text-black-600 text-2xl font-bold">Estamos em Manutenção</h1>
          <p className="text-gray-600 mt-2">
            Nosso sistema está passando por atualizações no momento.
            <br />
            Por favor, tente novamente mais tarde.
          </p>
        </div>
      </div>
    );
  };

  const isRotaPublica = window.location.href.indexOf('/public/') > -1;
  const loginRequired = !isRotaPublica ? 'login-required' : '';

  return process.env.REACT_APP_EM_MANUTENCAO === 'TRUE' ? (
    <MaintenancePage />
  ) : (
    <AppContext.Provider
      value={{
        routes,
      }}
    >
      <StylesProvider jss={jss} generateClassName={generateClassName}>
        <Provider store={store}>
          <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
            <ReactKeycloakProvider
              initOptions={{
                onLoad: loginRequired,
                silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
                checkLoginIframe: false,
                token: authService.getAccessToken(),
                refreshToken: authService.getRefreshToken(),
                redirectUri: `${process.env.REACT_APP_FRONT_URL}`,
              }}
              autoRefreshToken={false}
              authClient={keycloak}
              onTokens={handleTokenChange}
            >
              <Auth>
                <Router history={history}>
                  <RenderTheme theme={theme} />
                </Router>
              </Auth>
            </ReactKeycloakProvider>
          </MuiPickersUtilsProvider>
          <AppLoading />
        </Provider>
      </StylesProvider>
    </AppContext.Provider>
  );
};

const App = () => {
  return (
    <GlobalProvider>
      <AppContent />
    </GlobalProvider>
  );
};

export default App;
